import type { LinkResult, LinkView } from "../main/LinkController";
import { linkResultText, linkText } from "../strings";

function statusText(v: LinkView): { text: string; tone: "info" | "ok" | "waiting" | "error" } {
  switch (v.kind) {
    case "idle":
      return { text: "", tone: "info" };
    case "armed":
      return { text: linkText.armed, tone: "info" };
    case "binding":
      return { text: linkText.binding, tone: "info" };
    case "verifying":
      return { text: linkText.verifying(v.opponentJoined), tone: "info" };
    case "connecting":
      return { text: linkText.connecting, tone: "info" };
    case "handshake":
      return { text: linkText.handshake(v.stateLoaded), tone: "info" };
    case "running":
      if (v.desyncFrame !== null) return { text: linkText.desync, tone: "error" };
      if (v.stalled) return { text: v.lobby.hostMode === "server" ? linkText.reconnecting : linkText.stalled, tone: "waiting" };
      return { text: v.lobby.hostMode === "server" ? linkText.serverRunning : linkText.running(v.inputDelay), tone: "ok" };
    case "ended": {
      const parts = [linkText.ended(v.outcome, v.reason)];
      if (v.needsReload) parts.push(linkText.reload);
      else if (v.outcome !== "done") parts.push(linkText.stuck);
      return { text: parts.join(" "), tone: v.outcome === "done" ? "ok" : v.outcome === "desync" ? "error" : "waiting" };
    }
    case "error":
      return { text: v.message, tone: "error" };
  }
}

/** Link status under the game screen (WEB-20): connecting, delay, desync, ended, and the server's result. */
export function LinkStatusBar(props: { view: LinkView; result: LinkResult | null; onCancel: () => void; onDismiss: () => void }) {
  const { view, result } = props;
  if (view.kind === "idle" && !result) return null;
  const { text, tone } = statusText(view);
  const lobby = "lobby" in view ? view.lobby : null;
  const leaving = view.kind === "running" ? linkText.leave : linkText.cancel;
  const canCancel = view.kind !== "idle" && view.kind !== "ended" && view.kind !== "error";
  const finished = view.kind === "ended" || view.kind === "error" || view.kind === "idle";

  return (
    <div className="link-status" role="status" aria-live="polite">
      {lobby?.hostMode === "server" && (
        <span className="badge" title={linkText.serverBadgeHelp}>
          {linkText.serverBadge}
        </span>
      )}
      {text && <span className={`status ${tone}`}>{text}</span>}
      {result && <span className="status ok">{linkResultText(result)}</span>}
      {canCancel && <button onClick={props.onCancel}>{leaving}</button>}
      {finished && !(view.kind === "ended" && view.needsReload) && <button onClick={props.onDismiss}>{linkText.dismiss}</button>}
      {view.kind === "ended" && view.needsReload && <button onClick={() => window.location.reload()}>Reload</button>}
    </div>
  );
}
